import { useCallback, useEffect, useRef } from "react";

import { useTransformContext } from "react-zoom-pan-pinch";

import { usePlanContext } from "..";
import { getScaleAndImgSize } from "../utils";

const useResponsivePlan = (plan?: File) => {
  const instance = useTransformContext();

  const { imageProperties, setImageProperties } = usePlanContext("useResponsivePlan");

  const resizeTimeoutRef = useRef<number | null>(null);

  const updateDimensions = async (width: number, height: number) => {
    if (!plan || !instance.contentComponent) return { width: 0, height: 0 };

    const { scale, imgW, imgH } = await getScaleAndImgSize(plan, width, height);
    const newWidth = imgW * scale;
    const newHeight = imgH * scale;

    instance.contentComponent.style.width = `${newWidth}px`;
    instance.contentComponent.style.height = `${newHeight}px`;

    setImageProperties(prev => (prev ? { ...prev, width: newWidth, height: newHeight } : null));

    return { width: newWidth, height: newHeight };
  };

  const handleResize = useCallback(
    (entries: ResizeObserverEntry[]) => {
      const entry = entries[0];
      if (!entry || !instance.wrapperComponent || !plan) return;

      const { width, height } = entry.contentRect;

      if (
        imageProperties &&
        Math.abs(imageProperties.width - width) < 1 &&
        Math.abs(imageProperties.height - height) < 1
      )
        return;

      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);

      resizeTimeoutRef.current = window.setTimeout(() => {
        void updateDimensions(width, height).then(() => {
          //! PROBLEMS WITH ZOOM TO ELEMENTS
          //instance.getContext().centerView(undefined, 200);
          return undefined;
        });
      }, 100);
    },
    // oxlint-disable-next-line react/exhaustive-deps
    [plan],
  );

  useEffect(() => {
    if (!instance.wrapperComponent || !plan) return;

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(instance.wrapperComponent);

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [instance.wrapperComponent, plan, handleResize]);

  return { updateDimensions };
};

export { useResponsivePlan };
